import classNames from 'classnames'
import { CSSProperties, useMemo } from 'react'
import { RenderElementProps, useSlate } from 'slate-react'

import { Button } from 'core-components/library/button'
import {
  AbsolutePopup,
  PopPaper,
  TPopupHorizontal,
  TPopupVertical,
  TVisibilityTrigger,
} from 'core-components/library/popup'
import { ensureSafeUrl } from 'core-components/library/richTextEditor/helpers/urlHelper'
import { TLink, TLinkView } from 'core-components/library/richTextEditor/interface/Tlink'

import styles from './Link.module.scss'

interface IProps {
  link: TLink
  type: TLinkView
  onOpenLink: () => void
  removeLink: () => void
  className?: string
  style?: CSSProperties
}

const MAX_SHOW_URL_LENGTH = 50
const URL_SHOW_LENGTH = 25

export const LinkView = (props: RenderElementProps & IProps) => {
  const editor = useSlate()
  const shortUrlView = useMemo(() => {
    if (props.link.url.length > MAX_SHOW_URL_LENGTH) {
      return `${props.link.url.substring(0, URL_SHOW_LENGTH)}...${props.link.url.substring(
        props.link.url.length - URL_SHOW_LENGTH,
        props.link.url.length
      )}`
    }
    return props.link.url
  }, [props.link])

  const isImageLink = props.type === 'image'

  const isLinkType = props.type === 'text' || isImageLink

  const customLink = useMemo(() => {
    return isLinkType ? (
      <a
        className={classNames(props.className, styles.link)}
        style={props.style}
        href={props.element['url']}
        target={props.element['openInNewTab'] ? '_blank' : '_self'}
        rel="noreferrer"
        {...props.attributes}
      >
        {props.children}
      </a>
    ) : (
      <a
        href={props.element['url']}
        target={props.element['openInNewTab'] ? '_blank' : '_self'}
        rel="noreferrer"
        onBlur={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <Button
          iconPosition={props.element['iconPosition']}
          icon={props.element['icon']}
          className={props.className}
          {...props.attributes}
        >
          {props.children}
        </Button>
      </a>
    )
  }, [props])

  return editor.disabled ? (
    customLink
  ) : (
    <AbsolutePopup
      rootClassName={classNames(styles.root, isImageLink ? styles.root__image : '')}
      verticalDirection={TPopupVertical.Bottom}
      horizontalDirection={TPopupHorizontal.Right}
      verticalStartPosition={TPopupVertical.Bottom}
      horizontalStartPosition={TPopupHorizontal.Left}
      visibilityTrigger={TVisibilityTrigger.Hover}
      popupContent={() => {
        return (
          <PopPaper className={styles.popup}>
            <div
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <div className={styles.popup__rows}>
                <span>Go to link:</span>
                <a
                  href={ensureSafeUrl(props.element['url'])}
                  target={props.element['openInNewTab'] ? '_blank' : '_self'}
                  title={props.link.url}
                  rel="noreferrer"
                >
                  {shortUrlView}
                </a>
              </div>
              <div className={styles.popup__rows}>
                <Button
                  variant={'outlined'}
                  onClick={props.onOpenLink}
                  color={'primary'}
                  icon={'kbase kbase-edit'}
                >
                  Edit
                </Button>
                <Button
                  variant={'outlined'}
                  onClick={props.removeLink}
                  color={'danger'}
                  icon={'kbase kbase-delete'}
                >
                  Remove
                </Button>
              </div>
            </div>
          </PopPaper>
        )
      }}
    >
      {customLink}
    </AbsolutePopup>
  )
}
